/**
 * Phase 5 PR-A — Governance → Compliance sub-tab (v1).
 *
 * Per docs/SYSADMIN-PLATFORM-SPEC.md §3.5 + Phase 5 architect §2.1 +
 * founder OQ-5 (framework list LOCKED at v1):
 *
 *   - 5 framework cards: ITAR, CMMC L2, HIPAA, GDPR, EU AI Act
 *   - Per card: required controls count, retention days, witness
 *     requirement, attestations required
 *   - Edit-in-place: retention / witness / attestations
 *   - Required-controls list is read-only in v1 (changes to the
 *     regulatorily-binding mapping go through compliance officer
 *     review + the binding doc at COMPLIANCE-CONTROLS.md). v2 adds an
 *     editor.
 *   - NO "Add framework" button — OQ-5 LOCKED.
 */

import { useEffect, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import {
  Award, Shield, Clock, ScrollText, UserCheck, AlertTriangle,
  Save, RotateCcw, Inbox, FileText,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { SectionCard } from '@/components/ui/section-card';
import { SettingField } from '@/components/ui/setting-field';
import { StatusPill } from '@/components/ui/status-pill';
import { StatTile } from '@/components/ui/stat-tile';
import { EmptyState } from '@/components/ui/empty-state';
import { PageError } from '@/components/ui/page-error';
import {
  useSysadminComplianceProfiles,
  useSysadminCompliancePatch,
  type ComplianceProfile,
  type ComplianceFramework,
} from '@/lib/use-sysadmin-governance';

export const Route = createFileRoute('/sysadmin/governance/compliance')({
  component: CompliancePage,
});

// Human-display metadata for each framework.
const FRAMEWORK_META: Record<
  ComplianceFramework,
  { color: 'destructive' | 'warning' | 'info' | 'muted'; tagline: string }
> = {
  itar: {
    color: 'destructive',
    tagline: '22 CFR 120-130. US-only export controls; deemed export; strict liability.',
  },
  cmmc_l2: {
    color: 'info',
    tagline: 'DoD CMMC Level 2 — 110 NIST SP 800-171 practices.',
  },
  hipaa: {
    color: 'info',
    tagline: 'HIPAA PHI protection — 18 Safe Harbor identifiers; 6-year retention.',
  },
  gdpr: {
    color: 'info',
    tagline: 'EU privacy regulation — data subject rights; 72-hour breach notification.',
  },
  eu_ai_act: {
    color: 'warning',
    tagline: 'EU AI Act (Reg. 2024/1689) — risk-tiered AI obligations.',
  },
};

function CompliancePage() {
  const { data, isLoading, error, refetch, isFetching } =
    useSysadminComplianceProfiles();
  const profiles = data?.items ?? [];

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-5">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <Award className="h-7 w-7 text-muted-foreground" />
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">
              Compliance Profiles
            </h1>
            <p className="text-xs text-muted-foreground mt-1">
              Regulatory floor — 5 frameworks (locked at v1 per OQ-5). Tenants
              assign a profile and inherit its floor; they may raise individual
              settings, never lower.
            </p>
            <div className="flex items-center gap-2 mt-2">
              <Badge
                variant="outline"
                className="text-[10px] gap-1 font-mono"
              >
                <FileText className="h-2.5 w-2.5" />
                COMPLIANCE-CONTROLS.md
              </Badge>
              <Badge variant="secondary" className="text-[10px]">
                Slate locked (OQ-5)
              </Badge>
            </div>
          </div>
        </div>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <StatTile
          label="Frameworks"
          value={profiles.length}
          suffix="/ 5"
          tone={profiles.length === 5 ? 'success' : 'warning'}
          icon={Award}
        />
        <StatTile
          label="Controls (total)"
          value={profiles.reduce((s, p) => s + p.requiredControls.length, 0)}
          tone="default"
          icon={ScrollText}
        />
        <StatTile
          label="ITAR retention"
          value={
            profiles.find((p) => p.framework === 'itar')?.retentionDays ?? 0
          }
          suffix="days"
          tone="destructive"
          icon={Clock}
        />
        <StatTile
          label="HIPAA retention"
          value={
            profiles.find((p) => p.framework === 'hipaa')?.retentionDays ?? 0
          }
          suffix="days"
          tone="default"
        />
        <StatTile
          label="Witness required"
          value={profiles.filter((p) => p.witnessRequired).length}
          suffix={`/ ${profiles.length}`}
          tone={
            profiles.some((p) => p.witnessRequired) ? 'warning' : 'muted'
          }
          icon={Shield}
        />
      </div>

      {error && (
        <PageError
          title="Failed to load compliance profiles"
          message={error.message}
          onRetry={refetch}
          retrying={isFetching}
        />
      )}

      {!error && (
        <>
          {isLoading && profiles.length === 0 ? (
            <div className="py-10 text-center text-muted-foreground text-sm animate-pulse">
              Loading compliance profiles…
            </div>
          ) : profiles.length === 0 ? (
            <EmptyState
              icon={Inbox}
              title="No compliance profiles seeded"
              body="Migration bA13 seeds the 5 framework profiles from COMPLIANCE-CONTROLS.md. Apply the migration if you're seeing this."
            />
          ) : (
            <div className="space-y-5">
              {profiles.map((p) => (
                <FrameworkEditor key={p.framework} profile={p} />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ─── Framework editor ──────────────────────────────────────────────────────

function FrameworkEditor({ profile }: { profile: ComplianceProfile }) {
  const patch = useSysadminCompliancePatch();
  const [draft, setDraft] = useState({
    retentionDays: profile.retentionDays,
    witnessRequired: profile.witnessRequired,
    description: profile.description,
    attestationsRequired: profile.attestationsRequired.join(', '),
  });

  useEffect(() => {
    setDraft({
      retentionDays: profile.retentionDays,
      witnessRequired: profile.witnessRequired,
      description: profile.description,
      attestationsRequired: profile.attestationsRequired.join(', '),
    });
  }, [profile]);

  const dirty =
    draft.retentionDays !== profile.retentionDays ||
    draft.witnessRequired !== profile.witnessRequired ||
    draft.description !== profile.description ||
    draft.attestationsRequired !==
      profile.attestationsRequired.join(', ');

  const handleApply = () => {
    const attestations = draft.attestationsRequired
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
    patch.mutate({
      framework: profile.framework,
      body: {
        retentionDays: draft.retentionDays,
        witnessRequired: draft.witnessRequired,
        description: draft.description,
        attestationsRequired: attestations,
      },
    });
  };

  const handleDiscard = () =>
    setDraft({
      retentionDays: profile.retentionDays,
      witnessRequired: profile.witnessRequired,
      description: profile.description,
      attestationsRequired: profile.attestationsRequired.join(', '),
    });

  const meta = FRAMEWORK_META[profile.framework];
  const familyCounts = countControlsByFamily(profile.requiredControls);

  return (
    <SectionCard
      title={profile.displayName}
      description={meta.tagline}
      icon={Award}
      actions={
        <div className="flex items-center gap-2">
          <StatusPill status={meta.color} icon={Award} compact>
            v{profile.version}
          </StatusPill>
          {profile.witnessRequired && (
            <StatusPill status="warning" icon={Shield} compact>
              Witness required
            </StatusPill>
          )}
          {dirty && (
            <>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleDiscard}
                disabled={patch.isPending}
              >
                <RotateCcw className="h-3.5 w-3.5 mr-1" />
                Discard
              </Button>
              <Button
                size="sm"
                onClick={handleApply}
                disabled={patch.isPending}
              >
                <Save className="h-3.5 w-3.5 mr-1" />
                {patch.isPending ? 'Saving…' : 'Apply'}
              </Button>
            </>
          )}
        </div>
      }
    >
      <SettingField
        label="Description"
        description="Human-readable summary shown to tenants when they select this profile."
      >
        <Input
          value={draft.description ?? ''}
          onChange={(e) =>
            setDraft({ ...draft, description: e.target.value || null })
          }
          maxLength={2000}
          className="h-9"
        />
      </SettingField>

      <SettingField
        label="Audit retention (days)"
        description={`Floor for audit-log retention. Tenants may retain longer, never shorter. Currently ${formatYears(draft.retentionDays)}.`}
        complianceCitation={retentionCitation(profile.framework)}
      >
        <Input
          type="number"
          min={30}
          max={36500}
          value={draft.retentionDays}
          onChange={(e) =>
            setDraft({ ...draft, retentionDays: Number(e.target.value) })
          }
          className="h-9 max-w-[160px]"
        />
      </SettingField>

      <SettingField
        label="External witness required"
        description="Hash-anchor the audit chain to an external witness service for tamper evidence."
      >
        <div className="flex items-center gap-2">
          <Switch
            checked={draft.witnessRequired}
            onCheckedChange={(c) =>
              setDraft({ ...draft, witnessRequired: c })
            }
            id={`witness-${profile.framework}`}
          />
          <Label
            htmlFor={`witness-${profile.framework}`}
            className="text-xs"
          >
            {draft.witnessRequired ? 'Required' : 'Optional'}
          </Label>
        </div>
      </SettingField>

      <SettingField
        label="Required user attestations"
        description="Comma-separated slugs (us_person_verified, baa_signed, gdpr_dpa_signed, eu_ai_role)."
      >
        <Input
          value={draft.attestationsRequired}
          onChange={(e) =>
            setDraft({ ...draft, attestationsRequired: e.target.value })
          }
          placeholder="us_person_verified, baa_signed"
          className="h-9 text-xs font-mono"
        />
      </SettingField>

      {/* Controls summary (read-only in v1) */}
      <div className="space-y-2 pt-2 border-t">
        <div className="flex items-center gap-2">
          <ScrollText className="h-3.5 w-3.5 text-muted-foreground" />
          <Label className="text-xs font-semibold">
            Required controls ({profile.requiredControls.length})
          </Label>
          <Badge variant="outline" className="text-[9px]">
            Read-only in v1
          </Badge>
        </div>
        <p className="text-[11px] text-muted-foreground">
          The control mapping is regulatorily binding (transcribed from{' '}
          <code className="text-[10px] bg-muted px-1 py-0.5 rounded">
            docs/COMPLIANCE-CONTROLS.md
          </code>
          ). Changes require compliance officer review; v2 ships an editor.
        </p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
          {Object.entries(familyCounts).length === 0 ? (
            <p className="text-muted-foreground italic">
              No control mapping captured.
            </p>
          ) : (
            Object.entries(familyCounts).map(([family, count]) => (
              <div
                key={family}
                className="px-3 py-2 border rounded-md bg-background"
              >
                <div className="text-[10px] text-muted-foreground uppercase tracking-wide">
                  {family}
                </div>
                <div className="text-base font-semibold tabular-nums">
                  {count}
                </div>
              </div>
            ))
          )}
        </div>

        {/* Show first 3 controls as a teaser */}
        {profile.requiredControls.slice(0, 3).map((c) => (
          <div
            key={c.id}
            className="px-3 py-2 border rounded-md text-xs space-y-1 bg-background"
          >
            <div className="flex items-center gap-2">
              <code className="text-[10px] bg-muted px-1.5 py-0.5 rounded font-mono">
                {c.id}
              </code>
              <span className="font-medium">{c.title}</span>
              {c.status && (
                <Badge variant="outline" className="text-[9px]">
                  {c.status}
                </Badge>
              )}
            </div>
            {c.mapping && (
              <p className="text-[10px] text-muted-foreground">{c.mapping}</p>
            )}
          </div>
        ))}
        {profile.requiredControls.length > 3 && (
          <p className="text-[11px] text-muted-foreground italic">
            …and {profile.requiredControls.length - 3} more. Full list
            available via API.
          </p>
        )}
      </div>

      {/* Attestations preview */}
      {profile.attestationsRequired.length > 0 && (
        <div className="pt-2 border-t">
          <div className="flex items-center gap-2 mb-2">
            <UserCheck className="h-3.5 w-3.5 text-muted-foreground" />
            <Label className="text-xs font-semibold">
              Currently required attestations
            </Label>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {profile.attestationsRequired.map((a) => (
              <Badge key={a} variant="outline" className="text-[10px]">
                {a}
              </Badge>
            ))}
          </div>
        </div>
      )}

      {patch.isError && (
        <PageError
          title="Failed to save compliance profile"
          message={patch.error?.message ?? 'Unknown error'}
          onRetry={handleApply}
          retrying={patch.isPending}
        />
      )}
    </SectionCard>
  );
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function countControlsByFamily(
  controls: ComplianceProfile['requiredControls'],
): Record<string, number> {
  const out: Record<string, number> = {};
  for (const c of controls) {
    const fam = c.family ?? deriveFamily(c.id);
    out[fam] = (out[fam] ?? 0) + 1;
  }
  return out;
}

/**
 * Derive a family label when the control row didn't carry one
 * (ITAR + HIPAA rows use the id prefix as family).
 */
function deriveFamily(id: string): string {
  if (id.startsWith('I-')) return 'ITAR';
  if (id.startsWith('PHI-')) return 'PHI';
  if (id.startsWith('HIPAA-')) return 'HIPAA-SC';
  if (id.startsWith('GDPR-')) return 'GDPR';
  if (id.startsWith('EU-AI-')) return 'EU-AI';
  if (id.startsWith('AC.')) return 'AC';
  if (id.startsWith('AU.')) return 'AU';
  if (id.startsWith('SC.')) return 'SC';
  return 'Other';
}

function formatYears(days: number): string {
  const years = days / 365;
  if (years >= 1) return `${years.toFixed(1)}y`;
  return `${days}d`;
}

function retentionCitation(framework: ComplianceFramework): string {
  switch (framework) {
    case 'itar':
      return '22 CFR 122.5 — minimum 5 years (1825 days).';
    case 'cmmc_l2':
      return 'CMMC L2 AU.L2-3.3.9 — minimum 3 years (1095 days).';
    case 'hipaa':
      return 'HIPAA 164.530(j) — minimum 6 years (2190 days).';
    case 'gdpr':
      return 'GDPR Art. 30 — record-keeping; 7 years aligns with AML retention.';
    case 'eu_ai_act':
      return 'EU AI Act Art. 12 — 10 years for high-risk systems.';
    default:
      return '';
  }
}
